import React from 'react';
import { IHealthCheckStatsResponse } from '../../store/types/healthCheck';
import { Paper, Typography, Box, Grid, Table, TableBody, TableCell, TableContainer, TableRow, Divider, useTheme } from '@mui/material';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

interface Props {
    stats: IHealthCheckStatsResponse;
}

const HealthCheckStats: React.FC<Props> = ({ stats }) => {
    const theme = useTheme();
    const session = stats.sessionStats[0];
    const event = stats.eventStats;
    // Key metrics
    const addToCartPct = event.customerToCartPct; // з респонсу
    // Для Success/Error картки
    const successPct = event.endStatusPctCat.success;
    const errorPct = event.endStatusPctCat.error;
    const notEndedPct = event.endStatusPctCat.notEnded;

    // actualMockupDurationAvgCat
    const actualMockupDurationRows = Object.entries(event.actualMockupDurationAvgCat).map(([k, v]) => (
        <TableRow key={k}><TableCell>{k}</TableCell><TableCell>{v} sec</TableCell></TableRow>
    ));
    // saveDurationAvgCat
    const saveDurationRows = Object.entries(event.saveDurationAvgCat).map(([k, v]) => (
        <TableRow key={k}><TableCell>{k}</TableCell><TableCell>{v} sec</TableCell></TableRow>
    ));

    // Pie: Success/Error/NotEnded
    const statusPieData = {
        labels: ['Success', 'Error', 'Not Ended'],
        datasets: [{
            data: [event.endStatusCountCat.success, event.endStatusCountCat.error, event.endStatusCountCat.notEnded],
            backgroundColor: ['#4caf50', '#f44336', '#ff9800'],
        }]
    };
    // Pie: Error breakdown
    const errorPieData = {
        labels: Object.keys(event.endStatusErrorPct),
        datasets: [{
            data: Object.values(event.endStatusErrorPct).map(Number),
            backgroundColor: ['#f44336', '#ff9800', '#2196f3'],
        }]
    };
    // Pie: NotEnded breakdown
    const notEndedPieData = {
        labels: ['Left <10s', 'Left 10-20s', 'Left >20s'],
        datasets: [{
            data: [event.endStatusNotEndedCat.leftBefore10sPct, event.endStatusNotEndedCat.leftAfter10sPct, event.endStatusNotEndedCat.leftAfter20sPct].map(Number),
            backgroundColor: ['#bdbdbd', '#90caf9', '#1976d2'],
        }]
    };
    // Bar: Time categories
    const barOptions = { responsive: true, plugins: { legend: { display: false }, title: { display: false } } };
    const barData = (labels: string[], data: Record<string, string>, title: string) => ({
        labels,
        datasets: [{
            label: title,
            data: labels.map(l => Number(data[l])),
            backgroundColor: ['#4caf50', '#ffb300', '#f44336', '#1976d2'],
        }]
    });

    // Функція для визначення кольору підсвічування
    const getHighlight = (type: string, value: number) => {
        const isDark = theme.palette.mode === 'dark';
        if (type === 'success') {
            if (value > 93) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9'; // light green
            if (value > 80) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7'; // light yellow
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee'; // light red
        }
        if (type === 'actualMockup') {
            if (value < 5.7) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9';
            if (value < 6.5) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7';
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee';
        }
        if (type === 'save') {
            if (value < 4) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9';
            if (value < 5) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7';
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee';
        }
        if (type === 'process') {
            if (value < 6) return isDark ? 'rgba(76, 175, 80, 0.2)' : '#e8f5e9';
            if (value < 7) return isDark ? 'rgba(255, 253, 231, 0.2)' : '#fffde7';
            return isDark ? 'rgba(244, 67, 54, 0.2)' : '#ffebee';
        }
        return theme.palette.background.paper;
    };

    // Значення для підсвічування
    const successValue = parseFloat(successPct);
    const actualMockupValue = parseFloat(event.actualMockupDurationAvg);
    const saveValue = parseFloat(event.saveDurationAvg);
    const processValue = parseFloat(event.processDurationAvg);

    // Основні метрики (8 штук)
    const metrics = [
        {
            value: event.eventCount,
            label: 'Event Count',
            highlight: theme.palette.background.paper,
        },
        {
            value: session.totalSessions,
            label: 'Total Sessions',
            highlight: theme.palette.background.paper,
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {session.sessionsWithAddToCart}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 6 }}>
                        ({addToCartPct}%)
                    </span>
                </span>
            ),
            label: 'Add To Cart',
            highlight: theme.palette.background.paper,
        },
        {
            value: session.sessionsWithOrders,
            label: 'Sessions With Orders',
            highlight: theme.palette.background.paper,
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{successPct}%</span>
            ),
            label: 'Success/Error',
            sub: (
                <span style={{ fontSize: 15, color: theme.palette.text.secondary, marginLeft: 0 }}>
                    Error: {errorPct}% | Not Ended: {notEndedPct}%
                </span>
            ),
            highlight: getHighlight('success', successValue),
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {event.actualMockupDurationAvg}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 4 }}>sec</span>
                </span>
            ),
            label: 'Actual Mockup Duration Avg',
            highlight: getHighlight('actualMockup', actualMockupValue),
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {event.saveDurationAvg}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 4 }}>sec</span>
                </span>
            ),
            label: 'Save Duration Avg',
            highlight: getHighlight('save', saveValue),
        },
        {
            value: (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {event.processDurationAvg}
                    <span style={{ fontSize: 18, color: theme.palette.text.secondary, marginLeft: 4 }}>sec</span>
                </span>
            ),
            label: 'Process Duration Avg',
            highlight: getHighlight('process', processValue),
        },
    ];

    return (
        <Box>
            {/* Key metrics */}
            <Typography variant="h6" sx={{ mb: 2 }}>Metrics</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="stretch" flexWrap="wrap">
                {metrics.slice(0, 4).map((m, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={m.label}>
                        <Paper sx={{ p: 2, minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, boxShadow: 2, backgroundColor: m.highlight }}>
                            <Typography sx={{ fontSize: 32, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.value}</Typography>
                            {m.sub && <Typography sx={{ fontSize: 15, color: theme.palette.text.secondary, mt: 0.5 }}>{m.sub}</Typography>}
                            <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary, mt: 1 }}>{m.label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="stretch" flexWrap="wrap">
                {metrics.slice(4, 8).map((m, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={m.label}>
                        <Paper sx={{ p: 2, minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, boxShadow: 2, backgroundColor: m.highlight }}>
                            <Typography sx={{ fontSize: 32, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.value}</Typography>
                            {m.sub && <Typography sx={{ fontSize: 15, color: theme.palette.text.secondary, mt: 0.5 }}>{m.sub}</Typography>}
                            <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary, mt: 1 }}>{m.label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1">actualMockupDurationAvgCat (sec)</Typography>
                        <TableContainer>
                            <Table size="small"><TableBody>{actualMockupDurationRows}</TableBody></Table>
                        </TableContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1">saveDurationAvgCat (sec)</Typography>
                        <TableContainer>
                            <Table size="small"><TableBody>{saveDurationRows}</TableBody></Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            {/* Visualization */}
            <Typography variant="h6" sx={{ mb: 2 }}></Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Success/Error/NotEnded</Typography>
                        <Pie data={statusPieData} />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Error breakdown</Typography>
                        <Pie data={errorPieData} />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">NotEnded breakdown</Typography>
                        <Pie data={notEndedPieData} />
                    </Paper>
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Process Duration %</Typography>
                        <Bar options={barOptions} data={barData(['fast','medium','slow','extraSlow'], event.processDurationPctCat, 'Process Duration %')} />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Mockup Duration %</Typography>
                        <Bar options={barOptions} data={barData(['fast','medium','slow','extraSlow'], event.mockupDurationPctCat, 'Mockup Duration %')} />
                    </Paper>
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Actual Mockup Duration %</Typography>
                        <Bar options={barOptions} data={barData(['fast','medium','slow','extraSlow'], event.actualMockupDurationPctCat, 'Actual Mockup Duration %')} />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2">Save Duration %</Typography>
                        <Bar options={barOptions} data={barData(['fast','medium','slow','extraSlow'], event.saveDurationPctCat, 'Save Duration %')} />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HealthCheckStats; 